// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {DelegatedDismissalRequestStatuses, AutomatedCommentSource} from '@github-ui/diff-lines/conversation-types'
import type {AutomatedComment} from '../types'

interface StatusMessageProps {
  automatedComment: AutomatedComment
  resourceName: string
}

export function StatusMessage({automatedComment, resourceName}: StatusMessageProps) {
  if (automatedComment.source === AutomatedCommentSource.LicenseCompliance) {
    return <LicenseComplianceStatusMessage automatedComment={automatedComment} resourceName={resourceName} />
  }

  return <DefaultStatusMessage automatedComment={automatedComment} resourceName={resourceName} />
}

function DefaultStatusMessage({automatedComment, resourceName}: StatusMessageProps) {
  const {delegatedDismissalEnabled, delegatedDismissalRequest, isDismissed, dismissalReason, dismissedBy} =
    automatedComment

  if (delegatedDismissalRequest) {
    const {requester, status, reason, reviewer} = delegatedDismissalRequest
    const reasonText = reason ? ` as ${reason}` : ''
    switch (status) {
      case DelegatedDismissalRequestStatuses.Pending:
        return (
          <span className="fgColor-muted f6">
            <span className="text-bold">{requester}</span> requested to dismiss{reasonText}
          </span>
        )
      case DelegatedDismissalRequestStatuses.Rejected:
        return reviewer ? (
          <span className="fgColor-muted f6">
            <span className="text-bold">{reviewer}</span> denied request to dismiss
          </span>
        ) : (
          <span className="fgColor-muted f6">Request to dismiss was denied</span>
        )
    }
  }

  if (isDismissed) {
    const dismissalReasonText = dismissalReason ? ` as ${dismissalReason}` : ''
    if (!dismissedBy) {
      return (
        <span className="fgColor-muted f6">
          This {resourceName} was dismissed{dismissalReasonText}
        </span>
      )
    } else {
      return (
        <span className="fgColor-muted f6">
          <span className="text-bold">{dismissedBy}</span> dismissed this {resourceName}
          {dismissalReasonText}
        </span>
      )
    }
  }

  if (delegatedDismissalEnabled) return null

  return (
    <span className="fgColor-muted f6">Dismissing the {resourceName} will mark this conversation as resolved.</span>
  )
}

function LicenseComplianceStatusMessage({automatedComment, resourceName}: StatusMessageProps) {
  const {delegatedDismissalRequest} = automatedComment

  if (!delegatedDismissalRequest) {
    return null
  }

  const {requester, status, reason} = delegatedDismissalRequest
  const reasonText = reason ? ` as ${reason}` : ''
  switch (status) {
    case DelegatedDismissalRequestStatuses.Pending:
      return (
        <span className="fgColor-muted f6">
          <span className="text-bold">{requester}</span> requested to close{reasonText}
        </span>
      )
    case DelegatedDismissalRequestStatuses.Rejected:
      return (
        <span className="fgColor-muted f6">
          <span className="text-bold">{requester}</span> denied request to close
        </span>
      )
    case DelegatedDismissalRequestStatuses.Approved:
      return (
        <span className="fgColor-muted f6">
          <span className="text-bold">{requester}</span> closed this {resourceName}
          {reasonText}
        </span>
      )
    default:
      // Handle unrecognized statuses (e.g., Cancelled) by showing no status message
      return null
  }
}
